/* Tradeline product page: gallery + lightbox, variant picker (Section Rendering swaps),
 * sticky add-to-cart, share, quantity calculator, complementary products, pickup,
 * recently viewed tracking, and quote-request prefill on contact pages.
 * Depends on window.Tradeline (tl-theme.js). No external libraries.
 */
(() => {
  const T = window.Tradeline || (window.Tradeline = {});
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Quote request prefill (any page with a contact form) ---------- */
  const prefillQuote = () => {
    const params = new URLSearchParams(window.location.search);
    let message = params.get('message');
    if (!message && params.get('product')) {
      const sku = params.get('sku');
      const qty = params.get('qty');
      message = params.get('product') + (sku ? ' (' + sku + ')' : '') + (qty ? ' x ' + qty : '');
    }
    if (!message) return;
    const form = document.querySelector('form#ContactForm, form#contact_form, form.contact-form, form[action*="/contact"]');
    if (!form) return;
    const body = form.querySelector('textarea[name="contact[body]"]') || form.querySelector('textarea');
    if (body && !body.value.trim()) body.value = message;
    const productField = form.querySelector('input[name="contact[product]"], input[name="contact[Product]"]');
    if (productField && !productField.value && params.get('product')) productField.value = params.get('product');
  };

  /* ---------- Gallery ---------- */
  class TlGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.index = 0;

      this.addEventListener('click', (e) => this.onClick(e));
      this.track.addEventListener('keydown', (e) => {
        if (e.target !== this.track) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.setActive(Number(entry.target.dataset.index));
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => this.observer.observe(s));

      const start = this.dataset.startMedia;
      if (start) this.showMedia(start, false);
      this.setupLightbox();
    }

    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }

    onClick(e) {
      const goto = e.target.closest('[data-goto]');
      if (goto) { this.go(Number(goto.dataset.goto)); return; }
      if (e.target.closest('[data-gallery-prev]')) { this.go(this.index - 1); return; }
      if (e.target.closest('[data-gallery-next]')) { this.go(this.index + 1); return; }
      const deferred = e.target.closest('[data-deferred-load]');
      if (deferred) { this.loadDeferred(deferred.closest('[data-deferred]')); return; }
      const zoom = e.target.closest('[data-zoom]');
      if (zoom) this.openLightbox(zoom);
    }

    go(i, smooth = true) {
      const n = this.slides.length;
      if (!n) return;
      const target = (i + n) % n;
      const slide = this.slides[target];
      // Measured from the viewport, so it works the same in right-to-left layouts.
      const delta = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollBy({ left: delta, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      this.setActive(target);
    }

    showMedia(mediaId, smooth = true) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      if (this.classList.contains('tl-gallery--thumbs-stacked') && window.matchMedia('(min-width: 990px)').matches) {
        if (this.track.firstElementChild !== slide) this.track.prepend(slide);
        return;
      }
      this.go(Number(slide.dataset.index), smooth);
    }

    setActive(i) {
      if (Number.isNaN(i)) return;
      const changed = i !== this.index;
      this.index = i;
      this.querySelectorAll('[data-goto]').forEach((b) => {
        if (Number(b.dataset.goto) === i) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      const thumb = this.querySelector('.tl-gallery__thumb[aria-current="true"]');
      if (thumb && changed) {
        const strip = thumb.closest('.tl-gallery__thumbs');
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const t = thumb.getBoundingClientRect();
          const r = strip.getBoundingClientRect();
          strip.scrollBy({ left: (t.left + t.width / 2) - (r.left + r.width / 2), behavior: 'smooth' });
        }
        if (strip && strip.scrollHeight > strip.clientHeight) strip.scrollTo({ top: thumb.offsetTop - strip.clientHeight / 2 + thumb.clientHeight / 2, behavior: 'smooth' });
      }
      if (changed) this.pauseOthers();
    }

    pauseOthers() {
      this.slides.forEach((s, idx) => {
        if (idx === this.index) return;
        s.querySelectorAll('video').forEach((v) => v.pause());
        s.querySelectorAll('iframe').forEach((f) => {
          // Stop third-party players by reloading them as a poster again.
          const holder = f.closest('[data-deferred]');
          if (holder && holder._poster) { f.remove(); holder.appendChild(holder._poster); holder._poster = null; }
        });
      });
    }

    loadDeferred(holder) {
      if (!holder) return;
      const tpl = holder.querySelector('template');
      const poster = holder.querySelector('[data-deferred-load]');
      if (!tpl || !poster) return;
      const content = tpl.content.firstElementChild.cloneNode(true);
      holder._poster = poster;
      poster.remove();
      holder.appendChild(content);
      if (holder.dataset.deferred === 'model') {
        const start = () => {
          if (window.Shopify && Shopify.ModelViewerUI && content.tagName === 'MODEL-VIEWER') {
            try { new Shopify.ModelViewerUI(content); } catch (e) { /* UI optional */ }
          }
        };
        if (window.Shopify && typeof Shopify.loadFeatures === 'function') {
          Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: (err) => { if (!err) start(); } }]);
        }
        holder._poster = null;
      }
      const focusTarget = content.matches('iframe, model-viewer, video') ? content : content.querySelector('iframe, model-viewer, video');
      if (focusTarget) { focusTarget.setAttribute('tabindex', '0'); focusTarget.focus(); }
    }

    /* Lightbox (native <dialog>: focus is contained and Escape closes it) */
    setupLightbox() {
      this.lightbox = this.querySelector('[data-lightbox]');
      if (!this.lightbox) return;
      this.zoomables = Array.from(this.querySelectorAll('[data-zoom]'));
      this.lbImg = this.lightbox.querySelector('[data-lightbox-img]');
      this.lbCount = this.lightbox.querySelector('[data-lightbox-count]');
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox || e.target.closest('[data-lightbox-close]')) { this.lightbox.close(); return; }
        if (e.target.closest('[data-lightbox-prev]')) { this.lbShow(this.lbIndex - 1); return; }
        if (e.target.closest('[data-lightbox-next]')) { this.lbShow(this.lbIndex + 1); return; }
        if (e.target === this.lbImg) this.toggleZoom(e);
      });
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.lbShow(this.lbIndex + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.lbShow(this.lbIndex - 1); }
      });
      this.lbImg.addEventListener('mousemove', (e) => { if (this.lbImg.classList.contains('is-zoomed')) this.setOrigin(e); });
      this.lightbox.addEventListener('close', () => {
        document.documentElement.classList.remove('tl-locked');
        this.lbImg.classList.remove('is-zoomed');
        if (this.lbOpener) this.lbOpener.focus();
      });
    }

    openLightbox(button) {
      if (!this.lightbox) return;
      this.lbOpener = button;
      this.lbShow(this.zoomables.indexOf(button));
      document.documentElement.classList.add('tl-locked');
      if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
      else this.lightbox.setAttribute('open', '');
      const close = this.lightbox.querySelector('[data-lightbox-close]');
      if (close) close.focus();
    }

    lbShow(i) {
      const n = this.zoomables.length;
      if (!n) return;
      this.lbIndex = (i + n) % n;
      const btn = this.zoomables[this.lbIndex];
      this.lbImg.classList.remove('is-zoomed');
      this.lbImg.src = btn.dataset.src;
      this.lbImg.alt = btn.dataset.alt || '';
      if (this.lbCount) this.lbCount.textContent = (this.lbIndex + 1) + ' / ' + n;
      this.lightbox.querySelectorAll('[data-lightbox-prev], [data-lightbox-next]').forEach((b) => { b.hidden = n < 2; });
    }

    toggleZoom(e) {
      this.setOrigin(e);
      this.lbImg.classList.toggle('is-zoomed');
    }

    setOrigin(e) {
      const r = this.lbImg.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      this.lbImg.style.transformOrigin = x + '% ' + y + '%';
    }
  }

  /* ---------- Product (variant switching and the info column) ---------- */
  class TlProduct extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.form = document.getElementById(this.dataset.form);
      this.gallery = this.querySelector('tl-gallery');
      this.cache = new Map();
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      const idInput = this.idInput();
      this.variant = this.variants.find((v) => String(v.id) === (idInput && idInput.value)) || this.variants[0];

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('input', (e) => { if (e.target.matches('[data-calc-input]')) this.calculate(e.target, false); });

      if (this.form) {
        // Guard: no cart in catalogue/quote modes or when prices are hidden.
        this.form.addEventListener('submit', (e) => {
          if (!this.form.querySelector('[data-atc]') && !document.querySelector(`[form="${this.form.id}"][type="submit"]:not([disabled])`)) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        });
      }

      if (T.recentlyViewed && this.dataset.handle) T.recentlyViewed.add(this.dataset.handle);
      this.setupSticky();
      this.loadPickup();
      this.loadComplementary();
      this.updateTotal();
    }

    disconnectedCallback() { if (this.stickyObserver) this.stickyObserver.disconnect(); }

    idInput() { return this.form ? this.form.querySelector('[data-variant-id]') : null; }

    qtyInput() { return this.querySelector('[data-qty-input]'); }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-option-index]')) { this.onOptionChange(t); return; }
      if (t.matches('[data-sticky-variant]')) {
        const v = this.variants.find((x) => String(x.id) === t.value);
        if (v) this.setVariant(v);
        return;
      }
      if (t.matches('[data-calc-input]')) { this.calculate(t, true); return; }
      if (t.matches('[data-qty-input], input[name="tl-addon"]')) this.updateTotal();
    }

    onClick(e) {
      const share = e.target.closest('[data-share]');
      if (share) { this.share(share); return; }
      const quote = e.target.closest('[data-quote-link]');
      if (quote) { this.quote(e, quote); return; }
      if (e.target.closest('[data-sticky-quote]')) {
        const link = this.querySelector('[data-quote-link]');
        if (link) link.click();
      }
    }

    selectedOptions() {
      const out = [];
      this.querySelectorAll('[data-option-index]').forEach((el) => {
        const i = Number(el.dataset.optionIndex);
        if (el.tagName === 'SELECT') out[i] = el.value;
        else if (el.checked) out[i] = el.value;
      });
      return out;
    }

    onOptionChange(input) {
      // Combined listings (Shopify Plus): an option value can belong to a sibling product.
      // Load that product's section in place instead of looking for a variant here.
      const source = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
      const target = source && source.dataset.productUrl;
      if (target && new URL(target, window.location.origin).pathname !== new URL(this.productUrl, window.location.origin).pathname) {
        this.switchProduct(target, { name: input.name, value: input.value, tag: input.tagName });
        return;
      }
      const opts = this.selectedOptions();
      const changed = Number(input.dataset.optionIndex);
      const value = input.value;
      // Live label update for button pickers.
      const fieldset = input.closest('fieldset');
      const label = fieldset && fieldset.querySelector('.tl-picker__selected');
      if (label) label.textContent = value;

      let v = this.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      if (!v) {
        // No exact combination: keep the option the shopper just picked and choose the best match.
        const matching = this.variants.filter((x) => x.options[changed] === value);
        v = matching.find((x) => x.available) || matching[0];
      }
      if (!v) { this.markUnavailable(); return; }
      this.setVariant(v, input);
    }

    markUnavailable() {
      this.querySelectorAll('[data-atc], .tl-sticky-atc__btn[type="submit"]').forEach((b) => {
        b.disabled = true;
        b.textContent = T.strings.unavailable || '';
      });
    }

    async setVariant(variant, focusEl) {
      this.variant = variant;
      const idInput = this.idInput();
      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const sticky = this.querySelector('[data-sticky-variant]');
      if (sticky) sticky.value = String(variant.id);
      if (this.gallery && variant.media && this.gallery.showMedia) this.gallery.showMedia(variant.media);
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + window.location.hash);
      this.updateTotal();
      this.loadPickup();

      const focusKey = focusEl ? { name: focusEl.name, value: focusEl.value, tag: focusEl.tagName } : null;
      this.classList.add('is-updating');
      try {
        const html = await this.fetchVariantHTML(variant.id);
        if (this.variant !== variant) return; // a newer selection superseded this one
        this.swapRegions(T.parseHTML ? T.parseHTML(html) : new DOMParser().parseFromString(html, 'text/html'));
        if (focusKey) this.restoreFocus(focusKey);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('[Tradeline] variant update failed', err);
      } finally {
        if (this.variant === variant) this.classList.remove('is-updating');
      }
      T.emit && T.emit('tl:variant:changed', { variant, sectionId: this.sectionId });
    }

    async switchProduct(productUrl, focusKey) {
      this.classList.add('is-updating');
      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('tl-product');
        if (!fresh) throw new Error('no product section');
        const pageUrl = new URL(productUrl, window.location.origin);
        window.history.pushState({ tlProductSwap: true }, '', pageUrl.pathname + pageUrl.search);
        if (!window.__tlProductPop) {
          // Back/forward after a sibling swap: the page content no longer matches the URL, so reload it.
          window.__tlProductPop = true;
          window.addEventListener('popstate', () => window.location.reload());
        }
        const title = fresh.querySelector('h1');
        if (title) document.title = title.textContent.trim() + ' – ' + document.title.split(' – ').slice(-1)[0];
        this.replaceWith(fresh);
        if (focusKey) {
          const sel = focusKey.tag === 'SELECT'
            ? `select[name="${CSS.escape(focusKey.name)}"]`
            : `input[name="${CSS.escape(focusKey.name)}"][value="${CSS.escape(focusKey.value)}"]`;
          const el = fresh.querySelector(sel);
          if (el) el.focus({ preventScroll: true });
        }
      } catch (err) {
        window.location.href = productUrl;
      }
    }

    async fetchVariantHTML(id) {
      if (this.cache.has(id)) return this.cache.get(id);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', id);
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url.toString(), { signal: this.controller.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      this.cache.set(id, html);
      return html;
    }

    swapRegions(doc) {
      this.querySelectorAll('[data-variant-region]').forEach((el) => {
        const name = el.dataset.variantRegion;
        const fresh = doc.querySelector(`[data-variant-region="${CSS.escape(name)}"]`);
        if (!fresh) return;
        if (el.hasAttribute('data-preserve')) this.carryState(el, fresh);
        el.innerHTML = fresh.innerHTML;
      });
      const qty = this.qtyInput();
      if (qty) this.clampQty(qty);
      this.updateTotal();
    }

    /* Keep what the shopper typed/picked (quantity, purchase option) across a region swap. */
    carryState(from, to) {
      from.querySelectorAll('input[name], select[name]').forEach((el) => {
        const sel = `[name="${CSS.escape(el.name)}"]`;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (!el.checked) return;
          to.querySelectorAll(sel).forEach((x) => {
            if (x.value === el.value && !x.disabled) x.setAttribute('checked', '');
            else if (x.type === 'radio') x.removeAttribute('checked');
          });
        } else if (el.tagName === 'SELECT') {
          const match = to.querySelector(sel);
          if (match) match.querySelectorAll('option').forEach((o) => { if (o.value === el.value) o.setAttribute('selected', ''); else o.removeAttribute('selected'); });
        } else if (el.type !== 'hidden') {
          const match = to.querySelector(sel);
          if (match) match.setAttribute('value', el.value);
        }
      });
    }

    clampQty(input) {
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step || 1);
      let v = Number(input.value || min);
      if (v < min) v = min;
      if (v > max) v = max;
      if (step > 1) v = min + Math.ceil((v - min) / step) * step;
      if (v > max) v = max;
      input.value = v;
    }

    restoreFocus(key) {
      const candidates = this.querySelectorAll(`[name="${CSS.escape(key.name)}"]`);
      const el = Array.from(candidates).find((c) => c.tagName === 'SELECT' || c.value === key.value);
      if (el) el.focus({ preventScroll: true });
    }

    /* ---------- Totals (frequently bought together + protection) ---------- */
    updateTotal() {
      const out = this.querySelector('[data-fbt-total]');
      if (!out || !this.variant) return;
      const addons = Array.from(this.querySelectorAll('input[name="tl-addon"]:checked'));
      if (!addons.length) { out.hidden = true; return; }
      const qtyEl = this.qtyInput();
      const qty = Math.max(1, Number(qtyEl ? qtyEl.value : 1) || 1);
      const total = this.variant.price * qty + addons.reduce((sum, a) => sum + Number(a.dataset.price || 0), 0);
      out.innerHTML = (out.dataset.template || '[amount]')
        .replace('[count]', String(addons.length + 1))
        .replace('[amount]', '<strong>' + T.formatMoney(total) + '</strong>');
      out.hidden = false;
    }

    /* ---------- Complementary products ---------- */
    loadComplementary() {
      const box = this.querySelector('[data-complementary]');
      if (!box) return;
      const load = async () => {
        try {
          const res = await fetch(box.dataset.url);
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const items = doc.querySelector('[data-fbt-items]');
          if (!items || !items.children.length) return;
          box.querySelector('[data-complementary-list]').innerHTML = items.innerHTML;
          box.hidden = false;
          this.updateTotal();
        } catch (e) { /* recommendations are optional */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((x) => x.isIntersecting)) { io.disconnect(); load(); }
        }, { rootMargin: '400px 0px' });
        // The box is hidden until loaded, so observe its parent block area instead.
        io.observe(box.parentElement || box);
      } else load();
    }

    /* ---------- Pickup availability ---------- */
    async loadPickup() {
      const slot = this.querySelector('[data-pickup]');
      if (!slot || !this.variant) return;
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', this.variant.id);
      url.searchParams.set('section_id', 'pickup-availability');
      const requested = this.variant.id;
      try {
        const res = await fetch(url.toString());
        if (!res.ok || requested !== this.variant.id) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const content = doc.querySelector('[data-pickup-content]');
        const drawer = doc.querySelector('[data-pickup-drawer]');
        const old = document.getElementById('PickupDrawer');
        if (old) old.remove();
        slot.innerHTML = '';
        if (!content) { slot.hidden = true; return; }
        slot.appendChild(document.adoptNode(content));
        // The drawer lives on <body> so it sits above the sticky info column and header.
        if (drawer) document.body.appendChild(document.adoptNode(drawer));
        slot.hidden = false;
      } catch (e) { /* pickup is optional */ }
    }

    /* ---------- Sticky add-to-cart ---------- */
    setupSticky() {
      this.sticky = this.querySelector('[data-sticky-atc]');
      const anchor = this.querySelector('[data-main-buy]');
      if (!this.sticky || !anchor || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.sticky.classList.toggle('is-visible', past);
        this.sticky.inert = !past;
        document.documentElement.classList.toggle('tl-has-sticky-atc', past);
      });
      this.stickyObserver.observe(anchor);
    }

    /* ---------- Share ---------- */
    async share(btn) {
      const url = btn.dataset.shareUrl || window.location.href;
      const title = btn.dataset.shareTitle || document.title;
      if (navigator.share) {
        try { await navigator.share({ title, url }); } catch (e) { /* cancelled */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch (e) {
        const tmp = document.createElement('textarea');
        tmp.value = url;
        tmp.setAttribute('readonly', '');
        tmp.style.position = 'fixed';
        tmp.style.opacity = '0';
        document.body.appendChild(tmp);
        tmp.select();
        try { document.execCommand('copy'); } catch (err) { /* ignore */ }
        tmp.remove();
      }
      if (T.toast) T.toast(btn.dataset.copied || url);
    }

    /* ---------- Quote request link (adds the chosen quantity) ---------- */
    quote(e, link) {
      const qtyEl = this.qtyInput();
      const qty = qtyEl ? Math.max(1, Number(qtyEl.value) || 1) : 1;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('qty', String(qty));
      const tpl = link.dataset.quoteTemplate;
      if (tpl) url.searchParams.set('message', tpl.replace('[title]', link.dataset.quoteName || '').replace('[qty]', String(qty)));
      e.preventDefault();
      window.location.href = url.toString();
    }

    /* ---------- Quantity calculator ---------- */
    calculate(input, apply) {
      const box = input.closest('[data-calc]');
      if (!box) return;
      const out = box.querySelector('[data-calc-result]');
      const need = Number(String(input.value).replace(',', '.'));
      const coverage = Number(box.dataset.coverage);
      if (!need || need <= 0 || !coverage) { if (out) out.textContent = ''; return; }
      const waste = Number(box.dataset.waste || 0);
      let units = Math.ceil((need * (1 + waste / 100)) / coverage - 1e-9);
      const qty = this.qtyInput();
      if (qty) {
        const min = Number(qty.min || 1);
        const step = Number(qty.step || 1);
        units = Math.max(units, min);
        if (step > 1) units = min + Math.ceil((units - min) / step) * step;
        if (qty.max) units = Math.min(units, Number(qty.max));
      }
      const covered = Math.round(units * coverage * 100) / 100;
      if (out) {
        out.textContent = (box.dataset.template || '')
          .replace('[qty]', String(units))
          .replace('[covered]', String(covered))
          .replace('[unit]', box.dataset.unit || '');
      }
      if (apply && qty && Number(qty.value) !== units) {
        qty.value = units;
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /* ---------- Gift card recipient (snippets/gift-card-recipient.liquid) ---------- */
  class TlGiftRecipient extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-gift-toggle]');
      this.fields = this.querySelector('[data-gift-fields]');
      this.email = this.querySelector('[data-gift-email]');
      this.error = this.querySelector('[data-gift-error]');
      this.offset = this.querySelector('[data-gift-offset]');
      if (!this.toggle || !this.fields) return;
      this.toggle.addEventListener('change', () => this.sync());
      if (this.email) this.email.addEventListener('input', () => { if (this.email.getAttribute('aria-invalid')) this.validate(); });
      this.form = this.toggle.form || this.closest('form');
      if (this.form) {
        // Capturing listener runs before tl-theme's document-level AJAX add to cart.
        this.onSubmit = (e) => {
          if (this.validate()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.email.focus();
        };
        this.form.addEventListener('submit', this.onSubmit, true);
      }
      this.sync();
    }

    disconnectedCallback() { if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true); }

    sync() {
      const on = this.toggle.checked;
      this.fields.hidden = !on;
      this.fields.querySelectorAll('input, textarea').forEach((el) => { el.disabled = !on; });
      if (this.email) this.email.required = on;
      if (this.offset) this.offset.value = String(new Date().getTimezoneOffset());
      if (!on) this.setError('');
    }

    validate() {
      if (!this.toggle.checked || !this.email) return true;
      const v = this.email.value.trim();
      const ok = v !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      this.setError(ok ? '' : this.dataset.msgEmail || '');
      return ok;
    }

    setError(msg) {
      if (!this.email) return;
      if (msg) this.email.setAttribute('aria-invalid', 'true'); else this.email.removeAttribute('aria-invalid');
      if (this.error) { this.error.textContent = msg; this.error.hidden = !msg; }
    }
  }

  if (!customElements.get('tl-gift-recipient')) customElements.define('tl-gift-recipient', TlGiftRecipient);
  if (!customElements.get('tl-gallery')) customElements.define('tl-gallery', TlGallery);
  if (!customElements.get('tl-product')) customElements.define('tl-product', TlProduct);

  /* ---------- Product tabs ----------
   * With the "tabs" row style, each run of two or more neighbouring collapsible rows
   * (description, specifications, shipping, custom rows) becomes one tab set. The rows stay
   * <details> in the HTML, so without JavaScript they are ordinary accordions.
   */
  let tabSetCount = 0;
  const buildTabs = (scope) => {
    (scope || document).querySelectorAll('[data-rows-style="tabs"]').forEach((info) => {
      const rows = Array.from(info.querySelectorAll('details.tl-acc')).filter((d) => !d.closest('.tl-ptabs'));
      const groups = [];
      rows.forEach((row) => {
        const last = groups[groups.length - 1];
        const prev = last && last[last.length - 1];
        if (prev && prev.nextElementSibling === row) last.push(row); else groups.push([row]);
      });
      groups.filter((g) => g.length > 1).forEach((group) => {
        const n = ++tabSetCount;
        const start = Math.max(0, group.findIndex((row) => row.open));
        const wrap = document.createElement('div');
        wrap.className = 'tl-ptabs';
        const list = document.createElement('div');
        list.className = 'tl-ptabs__list';
        list.setAttribute('role', 'tablist');
        group[0].before(wrap);
        wrap.append(list);
        const tabs = group.map((row, i) => {
          const tab = document.createElement('button');
          const body = row.querySelector('.tl-acc__body');
          const title = row.querySelector('.tl-acc__title');
          tab.type = 'button';
          tab.className = 'tl-ptabs__tab';
          tab.id = 'ProductTab-' + n + '-' + i;
          tab.setAttribute('role', 'tab');
          tab.textContent = title ? title.textContent.trim() : '';
          if (body) {
            body.id = body.id || 'ProductTabPanel-' + n + '-' + i;
            body.setAttribute('role', 'tabpanel');
            body.setAttribute('aria-labelledby', tab.id);
            body.tabIndex = 0;
            tab.setAttribute('aria-controls', body.id);
          }
          row.open = true;
          row.classList.add('tl-acc--tab');
          list.append(tab);
          wrap.append(row);
          return tab;
        });
        const select = (i, focus) => {
          tabs.forEach((tab, k) => {
            const on = k === i;
            tab.setAttribute('aria-selected', String(on));
            tab.tabIndex = on ? 0 : -1;
            group[k].hidden = !on;
          });
          if (focus) tabs[i].focus();
        };
        tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => select(i));
          tab.addEventListener('keydown', (e) => {
            const map = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
            if (!(e.key in map)) return;
            e.preventDefault();
            select((map[e.key] + tabs.length) % tabs.length, true);
          });
        });
        select(start);
        wrap.tlSelect = (row) => { const i = group.indexOf(row); if (i > -1) select(i); };
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => buildTabs());
  else buildTabs();
  document.addEventListener('shopify:section:load', (e) => buildTabs(e.target));

  /* Theme editor: open a collapsed row (or its tab) when one of its blocks is selected. */
  document.addEventListener('shopify:block:select', (e) => {
    const details = e.target.closest ? (e.target.matches('details') ? e.target : e.target.closest('details')) : null;
    if (!details) return;
    details.open = true;
    const tabsWrap = details.closest('.tl-ptabs');
    if (tabsWrap && tabsWrap.tlSelect) tabsWrap.tlSelect(details);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prefillQuote);
  else prefillQuote();
})();
